<template>
  <div class="bmi-calculator">
    <div class="inputs">
      <div class="input-group">
        <label
          for="height"
          class="input-group__label"
          >Рост</label
        >
        <TextInputCustom
          id="height"
          v-model="height"
          input-mode="numeric"
          :state="heightError ? 'error' : ''"
          type="number"
          max="300"
          min="1"
          class="input-group__input"
          placeholder="СМ"
          @keydown="(event) => onKeyDown(event, 'height')"
          @input="(value, event) => onInput(value, event, 'height')"
        />
      </div>

      <div class="input-group">
        <label
          for="weight"
          class="input-group__label"
          >Вес</label
        >
        <TextInputCustom
          id="weight"
          v-model="weight"
          type="number"
          :state="weightError ? 'error' : ''"
          input-mode="numeric"
          max="500"
          min="1"
          class="input-group__input"
          placeholder="КГ"
          @keydown="(event) => onKeyDown(event, 'weight')"
          @input="(value, event) => onInput(value, event, 'weight')"
        />
      </div>

      <div class="button-group">
        <button
          class="button-group__calculate-button"
          :disabled="!height || !weight"
          @click="calculateBMI"
        >
          {{ buttonTitle }}
        </button>

        <button
          class="button-group__reset-button"
          :disabled="!height || !weight"
          @click="reset"
        >
          <SvgAsset :content="restoreIconContent" />
        </button>
      </div>
    </div>

    <div class="results">
      <div class="results__title">Ваш ИМТ</div>
      <div
        v-if="bmiResult"
        class="results__scale"
      >
        <div
          ref="popup"
          class="results__scale-popup"
          :style="popupStyle"
        >
          {{ bmiPopupNumber }} кг/м²
        </div>
        <div
          class="results__scale-marker"
          :style="markerStyle"
        />
        <div class="results__scale-bar">
          <div
            v-for="rangeConfig in ranges"
            :key="rangeConfig.bmiStart"
            :style="{
              backgroundColor: rangeConfig.bgColor,
              width: rangeConfig.sectionEnd - rangeConfig.sectionStart + '%',
            }"
          />
        </div>
      </div>
      <p
        v-if="bmiResult"
        class="results__description"
      >
        Данное значение ИМТ соответствует
        <span class="results__description--bmi"> {{ bmiResult }} </span>
      </p>
    </div>
  </div>
</template>

<script lang="ts">
  import TextInputCustom from '@/components/TextInput/TextInput.vue';
  import SvgAsset from '@devhacker/shared/components/SvgAsset.vue';
  import restoreIconContent from '~/assets/images/restore.svg?raw';

  const DEFAULT_BUTTON_TITLE = 'Рассчитать';

  export default defineNuxtComponent({
    components: {
      TextInputCustom,
      SvgAsset,
    },

    data() {
      return {
        height: null as string | null,
        weight: null as string | null,

        heightError: false,
        weightError: false,
        bmi: null as number | null,

        markerStyle: {
          left: undefined,
          borderLeftWidth: undefined,
          borderRightWidth: undefined,
          visibility: 'hidden',
        } as Record<string, string | undefined>,
        popupStyle: {
          left: undefined,
          borderBottomLeftRadius: undefined,
          borderBottomRightRadius: undefined,
          visibility: 'hidden',
        } as Record<string, string | undefined>,

        buttonTitle: DEFAULT_BUTTON_TITLE,
        excludedRegex: /[^0-9]/,

        ranges: [
          { bmiStart: 0, bmiEnd: 18.5, sectionStart: 0, sectionEnd: 25.5, bgColor: '#fd4b4b' },
          { bmiStart: 18.5, bmiEnd: 25, sectionStart: 25.5, sectionEnd: 45, bgColor: '#65b846' },
          { bmiStart: 25, bmiEnd: 30, sectionStart: 45, sectionEnd: 60.5, bgColor: '#fbd958' },
          { bmiStart: 30, bmiEnd: 100, sectionStart: 60.5, sectionEnd: 100, bgColor: '#fd4b4b' },
        ],
      };
    },

    computed: {
      restoreIconContent() {
        return restoreIconContent;
      },
      bmiPopupNumber(): string | undefined {
        return this.bmi?.toFixed(2).replace('.', ',');
      },

      bmiResult(): string | undefined {
        if (!this.bmi) {
          return;
        }

        const bmiValue = Math.round(this.bmi * 100) / 100;

        if (bmiValue < 18.5) {
          return 'дефициту массы тела';
        } else if (bmiValue < 25) {
          return 'нормальной массе тела';
        } else if (bmiValue < 30) {
          return 'предожирению';
        } else if (bmiValue < 35) {
          return 'ожирению I степени';
        } else if (bmiValue < 40) {
          return 'ожирению II степени';
        } else {
          return 'ожирению III степени';
        }
      },
    },

    watch: {
      heightError(newValue) {
        newValue &&
          setTimeout(() => {
            this.heightError = false;
          }, 1000);
      },

      weightError(newValue) {
        newValue &&
          setTimeout(() => {
            this.weightError = false;
          }, 1000);
      },
    },

    methods: {
      onInput(value: string, event: InputEvent, model: string): void {
        const target = event.target as HTMLInputElement;
        const min = parseInt(target.min);
        const max = parseInt(target.max);
        // Значение ограничено минимальным и максимальным значением инпута
        // Если пытаются вставить текст то просто заменяем на минимум
        let newValue = parseInt(value.replace(this.excludedRegex, ''));

        newValue = Math.max(min, Math.min(newValue, max));

        if (value) {
          target.value = newValue.toString();
          (this as any)[model] = newValue.toString();

          if (value !== newValue.toString()) {
            (this as any)[model + 'Error'] = true;
          }
        }
      },

      onKeyDown(event: KeyboardEvent, model: string): void {
        const invalidKeys = ['-', '+', 'e', 'E', '.'];

        // Не допускаем ввод запрещенных символов или начала с нуля
        // Остальные символы(буквы, знаки и т.д.) фильтруются через input type=number
        if (invalidKeys.includes(event.key) || (!this.height && event.key === '0')) {
          (this as any)[model + 'Error'] = true;
          event.preventDefault();
          return;
        }

        const hasSelection = window.getSelection()?.type === 'Range';
        if (hasSelection) {
          return;
        }

        // Если не число или новое значение больше максимального значения не допускаем
        if (this.excludedRegex.test(event.key) && event.key.length === 1) {
          (this as any)[model + 'Error'] = true;
          event.preventDefault();
        }
      },

      calculateBMI(): void {
        if (!this.height || !this.weight) {
          this.bmi = 0;
          return;
        }

        const heightInMeters = parseInt(this.height) / 100;
        this.bmi = parseInt(this.weight) / heightInMeters ** 2;
        this.buttonTitle = 'Рассчитать заново';

        this.$nextTick(() => {
          // Дожидаемся обновления, т.к. в расчете позиции используется размер попапа
          this.setMarkerPosition();
        });
      },

      reset(): void {
        this.bmi = null;
        this.height = null;
        this.weight = null;

        this.buttonTitle = DEFAULT_BUTTON_TITLE;

        this.popupStyle.visibility = 'hidden';
        this.markerStyle.visibility = 'hidden';
      },

      setMarkerPosition(): void {
        if (this.bmi == null) {
          return;
        }

        let rangePercentage = 100;
        const popupWidth = (this.$refs.popup as HTMLDivElement)?.scrollWidth || 0;

        for (const rangeConfig of this.ranges) {
          if (this.bmi >= rangeConfig.bmiStart && this.bmi <= rangeConfig.bmiEnd) {
            const { bmiStart, sectionStart, sectionEnd, bmiEnd } = rangeConfig;
            rangePercentage = this.scaleNumberToRange(
              this.bmi,
              bmiStart,
              bmiEnd,
              sectionStart,
              sectionEnd,
            );
            break;
          }
        }

        // Находимся ли мы в углу шкалы
        const isArrowInCorner = rangePercentage < 1 || rangePercentage > 99;
        // Если находимся в углу шкалы то нужно выводить угловой вариант стрелки
        const borderOffset = isArrowInCorner ? 8 : 4;
        this.markerStyle = {
          borderLeftWidth: `${borderOffset}px`,
          borderRightWidth: `${borderOffset}px`,
          // Учитываем размер стрелки для позиции
          left: `calc(${rangePercentage}% - ${borderOffset}px)`,
          visibility: 'visible',
        };

        // Adjust the popup's position and radius
        this.popupStyle = {
          // Ограничиваем попап между началом и концом шкалы, с учетом его размера
          left: `max(min(calc(${rangePercentage}% - ${popupWidth / 2}px), calc(100% - ${
            // Subpixel rendering problem, so just make it go over the edges
            popupWidth - 1
          }px)), 0%)`,
          // Для попапа угловой промежуток больше, перекрывает пограничный кейс 1% и 99%
          borderBottomLeftRadius: rangePercentage < 2 ? '0px' : undefined,
          borderBottomRightRadius: rangePercentage > 98 ? '0px' : undefined,
          visibility: 'visible',
        };
      },

      scaleNumberToRange(
        value: number,
        originalRangeStart: number,
        originalRangeEnd: number,
        newRangeStart: number,
        newRangeEnd: number,
      ): number {
        return (
          ((value - originalRangeStart) / (originalRangeEnd - originalRangeStart)) *
            (newRangeEnd - newRangeStart) +
          newRangeStart
        );
      },
    },
  });
</script>

<style scoped lang="scss">
  .bmi-calculator {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 24px;
    padding: 0px;
    margin: 0px -16px 12px;
    background-color: #f5f7fa;
    border-radius: 8px;

    @include big-tablet {
      margin: 0 -24px 12px;
    }

    @include tablet {
      flex-direction: row;
      gap: 16px;
      padding: 32px 24px;
    }

    .inputs {
      display: flex;
      flex: 50%;
      flex-direction: column;
      gap: 16px;
      padding: 32px 16px 24px;

      @include tablet {
        padding: 0;
      }

      .input-group {
        display: flex;
        flex-direction: column;
        gap: 8px;

        :deep() {
          input[type='number']::-webkit-inner-spin-button,
          input[type='number']::-webkit-outer-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }

          input[type='number'] {
            -moz-appearance: textfield;
            appearance: textfield;
          }
        }

        &__label {
          @include normalText;
          font-family: $secondFont;
          font-weight: 600;
        }
      }

      .button-group {
        display: flex;
        gap: 24px;

        &__calculate-button {
          @include normalText;
          font-family: $mainFont;
          background-color: $secondary;
          border: none;
          border-radius: 2px;
          color: white;
          cursor: pointer;
          font-weight: 700;
          padding: 12px 24px;
          width: 100%;

          &:disabled {
            background-color: #d4d7de;
            cursor: default;
          }

          @include hover {
            &:not(:disabled) {
              background-color: #264eb1;
            }
          }
        }

        &__reset-button {
          all: unset;
          fill: #2962f9;
          cursor: pointer;
          display: flex;
          align-items: center;

          svg {
            width: 24px;
            height: 24px;
            fill: inherit;
          }

          &:disabled,
          &:disabled:hover {
            fill: #d4d7de;
            cursor: default;
          }

          &:hover {
            fill: #264eb1;
          }
        }
      }
    }

    .results {
      display: flex;
      flex: 50%;
      flex-direction: column;
      gap: 16px;
      padding: 20px 16px;
      background-color: white;
      border-top: 2px solid $secondary;

      @include tablet {
        padding: 20px 16px;
      }

      &__title {
        @include mediumText;
        font-family: $secondFont;
        font-weight: 600;
        align-self: center;
        margin-bottom: 4px;

        @include tablet {
          @include bigText;
        }
      }

      &__scale {
        display: flex;
        flex-direction: column;
        overflow: hidden;

        &-popup {
          @include smallText;
          font-family: $secondFont;
          font-weight: 600;
          position: relative;
          width: fit-content;
          background-color: #222;
          padding: 8px 16px;
          border-radius: 4px;
          color: white;
        }

        &-marker {
          width: 0;
          height: 0;
          position: relative;
          border-style: solid;
          border-width: 8px 4px 0;
          border-color: #222 transparent transparent transparent;
        }

        &-bar {
          display: flex;
          border-radius: 28px;
          overflow: hidden;
          height: 8px;
          gap: 1px;
          margin-top: 4px;
        }
      }

      &__description {
        @include smallText;
        font-family: $mainFont;

        &--bmi {
          color: #2962f9;
          font-family: $secondFont;
          font-weight: 600;
        }
      }
    }
  }
</style>
